import axios from "axios";
import React, { useState, useEffect } from "react";

import {
  Button,
  Header,
  Icon,
  Image,
  Grid,
} from "semantic-ui-react";
import "../../node_modules/react-vis/dist/style.css";
import "../css/home.css";
import Constants from "../util/Constants";
import Utils from "../util/Utils";

// Breakpoint matching Semantic UI's "computer" threshold
const MOBILE_BREAKPOINT = 992;

// Match CompanyProfile page typography
const PROFILE_FONT = "'Lato', 'Helvetica Neue', Arial, sans-serif";
const TEXT_PRIMARY = "#0f1f35";
const TEXT_SECONDARY = "#3d5166";
const LINK_COLOR = "#1a4075";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return isMobile;
}

export default function CompanyHeader(props) {
  const isMobile = useIsMobile();

  const getButtonText = (isFavorite) => {
    return isFavorite ? "Remove from Favorites" : "Add to Favorites";
  };

  const initialButtonText = getButtonText(props.isFavorite);

  const [buttonText, setButtonText] = useState(initialButtonText);

  useEffect(() => {
    setButtonText(getButtonText(props.isFavorite));
  }, [props.isFavorite]);

  const handleFavorites = async () => {
    const { userEmail, stockValues, onFavoriteChange } = props;
    if (!stockValues || !stockValues.compProfile) return;
    if (!userEmail) {
      alert("Please sign in to manage favorites.");
      return;
    }

    const symbol = stockValues.compProfile.symbol;

    if (buttonText === "Add to Favorites") {
      await addToFavorites(userEmail, symbol, onFavoriteChange);
    } else if (buttonText === "Remove from Favorites") {
      await removeFromFavorites(userEmail, symbol, onFavoriteChange);
    }
  };

  const addToFavorites = async (userEmail, symbol, onFavoriteChange) => {
    const urlVal =
      Constants.ADD_TO_FAVORITES_URL +
      "?userEmail=" +
      encodeURIComponent(userEmail) +
      "&symbol=" +
      encodeURIComponent(symbol);

    try {
      await axios({
        method: "get",
        responseType: "json",
        url: urlVal,
      });
      setButtonText("Remove from Favorites");
      onFavoriteChange?.(true);
      alert("Symbol Added to Favorites");
    } catch (error) {
      alert("Unable to add Symbol to Favorites");
      console.log("Home:compStcksSubmit app response Error:", error);
    }
  };

  const removeFromFavorites = async (userEmail, symbol, onFavoriteChange) => {
    const urlVal =
      Constants.REMOVE_FROM_FAVORITES_URL +
      "?userEmail=" +
      encodeURIComponent(userEmail) +
      "&symbol=" +
      encodeURIComponent(symbol);

    try {
      await axios({
        method: "get",
        responseType: "json",
        url: urlVal,
      });
      setButtonText("Add to Favorites");
      onFavoriteChange?.(false);
      alert("Symbol removed from Favorites");
    } catch (error) {
      alert("Unable to remove Symbol from Favorites");
      console.log("Home:compStcksSubmit app response Error:", error);
    }
  };

  if (
    !props.stockValues ||
    !props.stockValues.compProfile ||
    !props.stockValues.quote
  ) {
    return null;
  }

  const companyProfile = props.stockValues.compProfile;
  const quote = props.stockValues.quote;
  const stockReport = props.stockValues.stockReport;
  const notEnoughData = props.stockValues.notEnoughData;

  // Stamp image name from recommendation (e.g. "Strong-Fundamentals" → "Strong")
  const fundStampName = stockReport?.recommendation?.split("-")[0] || null;
  // Technical stamp: use -ta suffix so you can use separate images labeled "Technicals" (e.g. Strong-ta.png)
  const taRec = stockReport?.technicalRecommendation?.split("-")[0] || null;
  const taStampBase = taRec === "Avoid" ? "Weak" : taRec;
  const taStampName = taStampBase ? `${taStampBase}-ta` : null;

  let priceColor = "black";
  let iconName = "";
  if (quote.change > 0) {
    priceColor = "green";
    iconName = "triangle up";
  } else if (quote.change < 0) {
    priceColor = "red";
    iconName = "triangle down";
  }

  const sliderSettings = {
    start: parseFloat(quote.price),
    min: parseFloat(quote.yearLow),
    max: parseFloat(quote.yearHigh),
    step: 10,
    disabled: true,
  };

  // ─── MOBILE LAYOUT ────────────────────────────────────────────────────────
  if (isMobile) {
    const rowBorder = { borderTop: "1px solid rgba(34,36,38,.1)" };
    const labelStyle = { fontFamily: PROFILE_FONT, fontSize: "0.8rem", color: TEXT_SECONDARY, fontWeight: 600, marginBottom: "0.15em" };
    const valueStyle = { fontFamily: PROFILE_FONT, fontSize: "0.88rem", color: TEXT_PRIMARY, fontWeight: 700 };
    const mobileImageSize = 48;
    const mobileImageStyle = { width: mobileImageSize, height: mobileImageSize, objectFit: "contain", display: "block", flexShrink: 0 };

    return (
      <Grid className="company-profile-header" style={{ width: "100%", margin: 0, fontFamily: PROFILE_FONT }}>

        {/* Row 1: Logo | Company info | Stamps */}
        <Grid.Row style={{ paddingBottom: "0.4rem", paddingTop: "0.5rem" }}>
          <Grid.Column
            width={2}
            verticalAlign="middle"
            style={{ paddingRight: 0 }}
          >
            <Image
              src={companyProfile.image}
              rounded
              size="mini"
              style={{ flexShrink: 0, margin: 0 }}
            />
          </Grid.Column>

          <Grid.Column
            width={notEnoughData ? 14 : 11}
            verticalAlign="middle"
            style={{ paddingLeft: "0.5rem" }}
          >
            <Header as="h4" className="compact-metric" style={{ margin: 0, fontFamily: PROFILE_FONT }}>
              <Header.Content>
                <a
                  href={companyProfile.website || "#"}
                  rel="noopener noreferrer"
                  target="_blank"
                  style={{ wordBreak: "break-word", color: LINK_COLOR, fontWeight: 600 }}
                >
                  {(companyProfile.companyName || "").substring(0, 35)}
                  ({companyProfile.symbol})
                </a>
                <Header.Subheader style={{ marginTop: "0.1em", color: TEXT_SECONDARY }}>
                  {companyProfile.sector || companyProfile.finVizSector || "N/A"}
                  {" \u2192 "}
                  {(
                    companyProfile.industry ||
                    companyProfile.finVizIndustry ||
                    ""
                  ).substring(0, 20) || "N/A"}
                </Header.Subheader>
              </Header.Content>
            </Header>
          </Grid.Column>

          {!notEnoughData && (fundStampName || taStampName) && (
            <Grid.Column
              width={3}
              verticalAlign="middle"
              textAlign="right"
              style={{ paddingLeft: 0 }}
            >
              {fundStampName && (
                <div
                  role={props.onStampClick ? "button" : undefined}
                  tabIndex={props.onStampClick ? 0 : undefined}
                  onKeyDown={props.onStampClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); props.onStampClick(); } } : undefined}
                  onClick={props.onStampClick || undefined}
                  style={{ display: "flex", cursor: props.onStampClick ? "pointer" : undefined, lineHeight: 0 }}
                  title={props.onStampClick ? "Go to Fundamentals Analysis (Beta)" : undefined}
                >
                  <img src={`${process.env.PUBLIC_URL}/images/${fundStampName}.png`} alt="Fundamentals" className="company-header-image" style={mobileImageStyle} />
                </div>
              )}
              {taStampName && (
                <div
                  role={props.onTechnicalStampClick ? "button" : undefined}
                  tabIndex={props.onTechnicalStampClick ? 0 : undefined}
                  onKeyDown={props.onTechnicalStampClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); props.onTechnicalStampClick(); } } : undefined}
                  onClick={props.onTechnicalStampClick || undefined}
                  style={{ display: "flex", cursor: props.onTechnicalStampClick ? "pointer" : undefined, lineHeight: 0 }}
                  title={props.onTechnicalStampClick ? "Go to Technical Analysis (Beta)" : undefined}
                >
                  <img src={`${process.env.PUBLIC_URL}/images/${taStampName}.png`} alt="TA" className="company-header-image" style={mobileImageStyle} />
                </div>
              )}
            </Grid.Column>
          )}
        </Grid.Row>

        {/* Row 2: All metrics — Price | Change | 52 Wk | Fair Value */}
        <Grid.Row
          columns={notEnoughData ? 3 : 4}
          style={{ ...rowBorder, paddingTop: "0.45rem", paddingBottom: "0.45rem" }}
        >
          <Grid.Column textAlign="center" verticalAlign="middle" style={{ padding: "0 0.3rem" }}>
            <div style={labelStyle}>Price</div>
            <div style={valueStyle}>{Utils.format_currency_usd(quote.price)}</div>
          </Grid.Column>

          <Grid.Column textAlign="center" verticalAlign="middle" style={{ padding: "0 0.3rem" }}>
            <div style={labelStyle}>Change</div>
            <div style={{ ...valueStyle, color: priceColor }}>
              {iconName && <Icon name={iconName} size="small" fitted />}
              {quote.change !== undefined
                ? `${quote.change} (${quote.changesPercentage}%)`
                : "N/A"}
            </div>
          </Grid.Column>

          <Grid.Column textAlign="center" verticalAlign="middle" style={{ padding: "0 0.3rem" }}>
            <div style={labelStyle}>52 Wk</div>
            <div style={{ ...valueStyle, fontSize: "0.78rem" }}>
              ${Utils.format_number(sliderSettings.min)}
              {"\u2013"}$
              {Utils.format_number(sliderSettings.max)}
            </div>
          </Grid.Column>

          {!notEnoughData && (
            <Grid.Column textAlign="center" verticalAlign="middle" style={{ padding: "0 0.3rem" }}>
              <div
                role={props.onFairValueClick ? "button" : undefined}
                tabIndex={props.onFairValueClick ? 0 : undefined}
                onKeyDown={props.onFairValueClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); props.onFairValueClick(); } } : undefined}
                onClick={props.onFairValueClick || undefined}
                style={{
                  cursor: props.onFairValueClick ? "pointer" : undefined,
                }}
                title={props.onFairValueClick ? "Go to Intrinsic Value" : undefined}
              >
                <div style={labelStyle}>Fair Val.</div>
                <div style={valueStyle}>
                  {(stockReport && Utils.getFairPrice(stockReport.intrinsicValue)) || "N/A"}
                </div>
              </div>
            </Grid.Column>
          )}
        </Grid.Row>

        {/* Row 3: Favorites button — compact, vertically + horizontally centered */}
        {!notEnoughData && (
          <Grid.Row style={{ ...rowBorder, paddingTop: "0.4rem", paddingBottom: "0.5rem" }}>
            <Grid.Column
              width={16}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <Button
                size="mini"
                className="ui blue button favorites-btn"
                onClick={handleFavorites}
                style={{ whiteSpace: "nowrap", padding: "0.5em 0.9em", margin: 0 }}
              >
                <Icon name="like" />
                <span className="favorites-btn-text">{buttonText}</span>
              </Button>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    );
  }

  // ─── DESKTOP LAYOUT ───────────────────────────────────────────────────────
  // Column budget (16 total):
  //   notEnoughData:  logo(1) + name(4) + price(3) + change(3) + 52wk(3) + stamp(2)  = 16
  //   full data:      logo(1) + name(3) + price(2) + change(3) + 52wk(2) + fairval(2) + btn(2) + stamp(1) = 16
  const metricCellStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  };
  const desktopLabelStyle = { fontFamily: PROFILE_FONT, fontSize: "0.75rem", color: TEXT_SECONDARY, fontWeight: 500, marginBottom: "0.2em", whiteSpace: "nowrap" };
  const desktopValueStyle = { fontFamily: PROFILE_FONT, fontSize: "1rem", color: TEXT_PRIMARY, fontWeight: 700, whiteSpace: "nowrap" };
  // Uniform size for logo and stamps so all images align and look the same
  const HEADER_IMAGE_SIZE = 48;
  const headerImageStyle = {
    width: HEADER_IMAGE_SIZE,
    height: HEADER_IMAGE_SIZE,
    objectFit: "contain",
    display: "block",
    flexShrink: 0,
  };
  const compactColumnPadding = { paddingLeft: "0.35rem", paddingRight: "0.35rem" };

  // Flex layout: left block (logo + company), evenly distributed metrics, right block (button + stamps)
  const leftBlock = (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0, minWidth: 0 }}>
      <div style={{ width: HEADER_IMAGE_SIZE, height: HEADER_IMAGE_SIZE, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: 8 }}>
        <img src={companyProfile.image} alt="" className="company-header-image" style={{ ...headerImageStyle, borderRadius: 8, maxWidth: "100%", maxHeight: "100%" }} />
      </div>
      <Header as="h4" className="compact-metric" style={{ margin: 0, fontFamily: PROFILE_FONT }}>
        <Header.Content>
          <a href={companyProfile.website || "#"} rel="noopener noreferrer" target="_blank" style={{ wordBreak: "break-word", color: LINK_COLOR, fontWeight: 600 }}>
            {(companyProfile.companyName || "").substring(0, 35)}({companyProfile.symbol})
          </a>
          <Header.Subheader style={{ wordBreak: "break-word", marginTop: "0.02em", display: "block", color: TEXT_SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }} title={[companyProfile.sector || companyProfile.finVizSector || "N/A", companyProfile.industry || companyProfile.finVizIndustry || ""].filter(Boolean).join(" → ")}>
            {companyProfile.sector || companyProfile.finVizSector || "N/A"}
            {" \u2192 "}
            {(companyProfile.industry || companyProfile.finVizIndustry || "").trim() || "N/A"}
          </Header.Subheader>
        </Header.Content>
      </Header>
    </div>
  );

  const metricsBlock = (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-evenly", gap: "0.5rem", minWidth: 0 }}>
      <div style={{ ...metricCellStyle, flex: 1, minWidth: 0 }}>
        <div style={desktopLabelStyle}>Current Price</div>
        <div style={desktopValueStyle}>{Utils.format_currency_usd(quote.price)}</div>
      </div>
      <div style={{ ...metricCellStyle, flex: 1, minWidth: 0 }}>
        <div style={desktopLabelStyle}>Day Change</div>
        <div style={{ ...desktopValueStyle, color: priceColor }}>
          {iconName && <Icon name={iconName} size="small" fitted />}
          {quote.change !== undefined ? `${quote.change} (${quote.changesPercentage}%)` : "N/A"}
        </div>
      </div>
      <div style={{ ...metricCellStyle, flex: 1, minWidth: 0 }}>
        <div style={desktopLabelStyle}>52 Week Range</div>
        <div style={desktopValueStyle}>
          ${Utils.format_number(sliderSettings.min)}&ndash;${Utils.format_number(sliderSettings.max)}
        </div>
      </div>
      {!notEnoughData && (
        <>
          <div
            role={props.onFairValueClick ? "button" : undefined}
            tabIndex={props.onFairValueClick ? 0 : undefined}
            onKeyDown={props.onFairValueClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); props.onFairValueClick(); } } : undefined}
            onClick={props.onFairValueClick || undefined}
            style={{ ...metricCellStyle, flex: 1, minWidth: 0, cursor: props.onFairValueClick ? "pointer" : undefined }}
            title={props.onFairValueClick ? "Go to Intrinsic Value" : undefined}
          >
            <div style={desktopLabelStyle}>Fair Value</div>
            <div style={desktopValueStyle}>{(stockReport && Utils.getFairPrice(stockReport.intrinsicValue)) || "N/A"}</div>
          </div>
          <div style={{ ...metricCellStyle, flex: 1, minWidth: 0 }}>
            <Button size="small" className="ui blue button favorites-btn" onClick={handleFavorites} style={{ whiteSpace: "nowrap" }}>
              <Icon name="like" />
              <span className="favorites-btn-text">{buttonText}</span>
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const stampsBlock = !notEnoughData && (fundStampName || taStampName) && (
    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", flexShrink: 0 }}>
      {fundStampName && (
        <div role={props.onStampClick ? "button" : undefined} tabIndex={props.onStampClick ? 0 : undefined}
          onKeyDown={props.onStampClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); props.onStampClick(); } } : undefined}
          onClick={props.onStampClick || undefined} style={{ display: "flex", cursor: props.onStampClick ? "pointer" : undefined, lineHeight: 0 }}
          title={props.onStampClick ? "Go to Fundamentals Analysis (Beta)" : undefined}>
          <img src={`${process.env.PUBLIC_URL}/images/${fundStampName}.png`} alt="Fundamentals" className="company-header-image" style={headerImageStyle} />
        </div>
      )}
      {taStampName && (
        <div role={props.onTechnicalStampClick ? "button" : undefined} tabIndex={props.onTechnicalStampClick ? 0 : undefined}
          onKeyDown={props.onTechnicalStampClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); props.onTechnicalStampClick(); } } : undefined}
          onClick={props.onTechnicalStampClick || undefined} style={{ display: "flex", cursor: props.onTechnicalStampClick ? "pointer" : undefined, lineHeight: 0 }}
          title={props.onTechnicalStampClick ? "Go to Technical Analysis (Beta)" : undefined}>
          <img src={`${process.env.PUBLIC_URL}/images/${taStampName}.png`} alt="TA" className="company-header-image" style={headerImageStyle} />
        </div>
      )}
    </div>
  );

  return (
    <Grid className="company-profile-header" style={{ width: "100%", margin: 0, fontFamily: PROFILE_FONT }}>
      <Grid.Row style={{ padding: "0.4rem 0", display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
        {leftBlock}
        {metricsBlock}
        {stampsBlock}
      </Grid.Row>
    </Grid>
  );
}
